// What the service is doing, as opposed to what it is configured with: the
// connection, the version it runs, and how many lines it has sent that could
// not be read.
//
// Read-only by design. It lives on the Posting service tab, which holds no fields at
// all, so the page's one form — and its one save bar — stays whole on
// Settings. The status badge used to sit inside that form, which was always
// odd: nothing about it is editable.

import { Card } from "@pej/kore/components";
import { FormatRelativeTime } from "@pej/kore/intl";
import type { LocationListEntry } from "@/api/types";
import { SplitRow } from "@/lib/forms";
import { useNow } from "@/lib/use-now";
import { ServiceStatusBadge, serviceHealthStatus } from "./service-health";

export function ServiceStatusCard({ location }: { location: LocationListEntry }) {
    const now = useNow();
    const status = serviceHealthStatus(location.serviceLastSeenAt, now);
    return (
        <Card className="flex flex-col gap-4 p-5">
            <h2 className="text-base font-semibold">Posting service</h2>
            <SplitRow label="Status">
                <span className="flex items-center gap-2 text-sm text-kore-text-muted">
                    <ServiceStatusBadge status={status} />
                    {location.serviceLastSeenAt ? (
                        <>
                            last seen <FormatRelativeTime value={new Date(location.serviceLastSeenAt)} now={now} />
                        </>
                    ) : null}
                </span>
            </SplitRow>
            <SplitRow label="Version">
                <span className="text-sm text-kore-text-muted">{location.serviceVersion ?? "—"}</span>
            </SplitRow>
            <SplitRow label="Unreadable lines">
                <span className="text-sm text-kore-text-muted">
                    {location.parseErrorCount === 0
                        ? "None"
                        : // Cumulative since setup — there are no per-error
                          // timestamps — so it says "approx." rather than
                          // implying these are recent. The feed below shows
                          // which lines they actually were.
                          `Approx. ${location.parseErrorCount} since setup`}
                </span>
            </SplitRow>
        </Card>
    );
}
